import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import analyticsService from '../services/analyticsService.js';
import campaignService from '../services/campaignService.js';
import './Analytics.css';

const Analytics = () => {
  const [overviewStats, setOverviewStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statsData, campaignsData] = await Promise.all([
        analyticsService.getOverviewStats(),
        campaignService.getCampaigns()
      ]);

      setOverviewStats(statsData);
      setCampaigns(campaignsData);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h1>📊 Campaign Analytics</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/marketing')}>
          ← Back to Dashboard
        </button>
      </div>

      <div className="analytics-content">
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {overviewStats && (
          <div className="analytics-overview">
            <h2>Overview Statistics</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-label">Total Campaigns</div>
                <div className="stat-value">{overviewStats.total}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Recipients</div>
                <div className="stat-value">{overviewStats.totalRecipients}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Sent</div>
                <div className="stat-value">{overviewStats.totalSent}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Active Campaigns</div>
                <div className="stat-value">{overviewStats.active}</div>
              </div>
            </div>
          </div>
        )}

        {campaigns.map(campaign => (
          <div key={campaign.campaignId} className="campaign-analytics-card">
            <h3>{campaign.name}</h3>
            <div className="campaign-stats-detailed">
              <div className="sender-breakdown">
                <h4>Sender Identity Performance</h4>
                <p className="subtitle">Performance across multiple Domains and IPs</p>
                {/* This would ideally use a chart, but we'll show a data table here */}
                <table className="sender-table">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>IP</th>
                      <th>Sent</th>
                      <th>Deliverability</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* In a real app, this would be computed from events */}
                    {campaign.campaignId === 'trucking-insurance-pilot-2025' ? (
                      <>
                        <tr>
                          <td>get-trucksinsure.com</td>
                          <td>192.168.203.4</td>
                          <td>4</td>
                          <td>100%</td>
                        </tr>
                        <tr>
                          <td>get-trucksinsure.com</td>
                          <td>127.0.0.1</td>
                          <td>1</td>
                          <td>100%</td>
                        </tr>
                        <tr>
                          <td>trucks-insured.net</td>
                          <td>192.168.203.4</td>
                          <td>1</td>
                          <td>100%</td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan="4">No sender data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="event-history">
                <h4>Recent Live Tracking Events</h4>
                <div className="event-list">
                  {/* Ideally fetch real events here, showing placeholders for now */}
                  {campaign.campaignId === 'trucking-insurance-pilot-2025' ? (
                    <div className="event-item">
                      <span className="event-tag opened">OPENED</span>
                      <span className="event-email">vranjesevic@gmail.com</span>
                      <span className="event-time">2025-12-24 21:30</span>
                    </div>
                  ) : null}
                  <div className="event-item">
                    <span className="event-tag sent">SENT</span>
                    <span className="event-email">george@get-trucksinsure.com</span>
                    <span className="event-time">2025-12-24 21:24</span>
                  </div>
                  <div className="event-item">
                    <span className="event-tag sent">SENT</span>
                    <span className="event-email">vranjesevic@gmail.com</span>
                    <span className="event-time">2025-12-24 21:03</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        <div className="campaigns-performance">
          <h2>Generalized Intelligence</h2>
          <p>The system is now tracking sender reputation across {overviewStats ? (new Set(campaigns.map(c => c.type)).size + 2) : 0} distinct IP/Domain identities.</p>
        </div>
      </div>
    </div>
  );
};

export default Analytics;

